import React, { useCallback, useEffect, useRef } from 'react';
import { injectIntl } from '@kyruus/intl';
import querystring from 'querystring';

import { DynamicGmap } from '@kyruus/gmap';
import Theme from '@kyruus/ui-theme';
import useMediaQuery from '@mui/material/useMediaQuery';

import FacetConfigurator from '../shared/facet-configurator';

import { getUpdatedSearch } from '../behaviors/search-v9';
import {
  CLICK_PIN,
  UPDATE_SEARCH_PARAMS,
  CLOSE_SORT_AND_FILTER
} from '../machines/map-search-v9/map-search-actions';
import {
  ERROR,
  FETCH_PROVIDERS,
  FETCHING_PROVIDERS,
  FETCHING_PROVIDERS_AND_LOCATIONS,
  IDLE,
  INITIALIZING,
  NO_MLOC_DATA,
  NO_RESULTS,
  PROVIDER_DRAWER_OPEN,
  SORT_AND_FILTER_DRAWER_OPEN
} from '../machines/map-search-v9/map-search-states';

import LoadingOverlay from 'Src/shared/loading';
import MapErrorModal from 'Src/shared/map-error-modal';
import { SideDrawerFilterPanel } from 'Src/shared/SideDrawerFilterPanel';

import { messages } from './lib/messages';
import { messages as searchV9Messages } from '../search-v9/messages';
import { MapContainer } from './styles';

import { getLocationUrl } from './components/MapCard/utils';
import {
  CLICK_LOCATION_DETAILS,
  CLICK_MAP_LOCATION_PIN,
  FILTERED_FACETS,
  MAPS_FILTERED_FACETS
} from './lib/constants';

import SmallMediumView from './components/SmallMediumView';
import LargeView from './components/LargeView';
import { MapControls } from './components/MapControls';
import MapSearchMetas from './components/MapSearchMetas';
import { getSortOptions } from '../utils/search-common';
import {
  formatLocationForGoogleMap,
  getBackToListView,
  gMapLatLngForMloc
} from './lib/utils';

import useStreetViewClose from './hooks/streetview-close';
import { isCCF } from '../utils/cerner';
import { isProviderListMapsEnabled } from 'Common/config';
import { SmallMediumProvidersMapView } from 'Src/map-search-v9/SmallMediumProvidersMapView';

// query params to omit when building the 'back to search' link.
// includes map-search specific deep linking params
const OMIT_QUERY_PARAMS_BACK_TO_SEARCH = ['location_id'];

function MapSearch({
  intl,
  history,
  location = {},
  config,
  log,
  state,
  send,
  customerCode,
  productName,
  appSettings,
  match,
  slotsByProviderId,
  slotsStatusByProviderId,
  fetchPurposesByProviderId,
  fetchProviderAvailability,
  purposeOptionsByProviderId,
  availabilityError,
  purposesLoading
}) {
  // service is not ready on first render of the component, so can't useService here. can
  // once we upgrade to @xstate/react v1.x?
  // https://github.com/davidkpiano/xstate/issues/938
  const { searchParams, providers, locations, selectedLocation } =
    state.context;

  // this hook returns event handlers to register with @kryuus/gmap
  // it tracks street view open/close state and exposes a custom close button
  // (we block the default google UI to close street view because it clashes with our map UI)
  const {
    mapEvents: streetViewMapEvents,
    closeButton: streetViewCloseButton,
    isStreetViewOpen
  } = useStreetViewClose();

  const showMapInProviderList = isProviderListMapsEnabled(config);

  // Check if we should show the no results banner for provider list
  const shouldShowNoResultsBanner =
    showMapInProviderList &&
    state.matches(NO_RESULTS) &&
    state.context.searchParams.geo_bounding_box;

  const gmapRef = useRef(null);

  // store zoom value on load/zooming of maps
  const getMapZoomRefOnLoad = useRef(null);

  const facetConfigure = useRef(
    new FacetConfigurator(config, FILTERED_FACETS, false)
  );

  useEffect(() => {
    const query = querystring.parse((location.search || '').substring(1));
    if (query.filter) {
      log(MAPS_FILTERED_FACETS, { filters: JSON.stringify(query.filter) });
    }
    let params = {};
    if (query.geo_bounding_box) {
      params.geo_bounding_box = query.geo_bounding_box;
    }
    if (query.map_center) {
      params.map_center = query.map_center;
    }

    if (query.map_zoom) {
      params.map_zoom = query.map_zoom;
    }
    params.categories = query.categories;
    params.provider_fields = query.provider_fields;
    if (!showMapInProviderList) {
      params = {};
    }

    send({
      type: UPDATE_SEARCH_PARAMS,
      data: {
        ...(query.display_location && { display_location: null }),
        ...(query.location && { location: query.location }),
        ...(query.distance && { distance: query.distance }),
        filter: query.filter || [],
        sort: query.sort,
        ...params
      }
    });
  }, [location.search, send]);

  // re-center map on selected pin when we get back to idle state (after closing provider or filter drawer)
  useEffect(() => {
    if (
      state.matches(IDLE) &&
      selectedLocation &&
      gmapRef.current &&
      !showMapInProviderList
    ) {
      gmapRef.current.panTo(gMapLatLngForMloc(selectedLocation));
    }
  }, [state.value]);

  // disabled for V1 of map search https://kyruus.jira.com/browse/KENG-29840
  // const { onSearchThisAreaClick, onBoundsChanged, showSearchThisAreaCTA } =
  //   useSearchThisArea({ history, location, log });

  const { search: currentQuery = '' } = location;

  const searchSummary = {
    query_string: currentQuery
  };

  const sortOptions = getSortOptions(config, searchParams);

  const currentSearchQuery = querystring.parse(
    (location.search || '').substring(1)
  );

  // TODO should this be named something else? this should update BOTH the
  // base providers AND the locations in the machine, along with the searchParams
  // Needs to be wired up to the machine and update context correctly
  const getUpdatedLocations = (modifications) =>
    getUpdatedSearch(config, currentSearchQuery, location, modifications);

  const mapContainerRef = useCallback((node) => {
    if (node && node.scrollIntoView) {
      // scroll the mapContainer so its top is flush with the viewport
      // this achieves the effect of a 'full screen' map while preserving the customer header/footer on the page
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  });

  const onLocationLinkClick = (e) => {
    e.preventDefault();
    const { name, id } = selectedLocation;
    const locationUrl = getLocationUrl(name, id, currentQuery);
    log(CLICK_LOCATION_DETAILS);
    history.push(locationUrl);
  };

  const isScreenMediumOrBelow = useMediaQuery(
    `(max-width:${Theme.screen_medium})`
  );

  const onPinClick = (e) => {
    const {
      marker: { map },
      meta: { location }
    } = e;
    gmapRef.current = map;
    const selectedMlocId = location.id;
    const mloc = locations._locations.find((loc) => loc.id === selectedMlocId);
    if (mloc) {
      // opens the provider drawer
      send({
        type: CLICK_PIN,
        data: mloc
      });

      // recenter map
      map.panTo(gMapLatLngForMloc(mloc));
      // pan map to put the clicked pin in the viewport after the provider drawer has covered the map
      if (isScreenMediumOrBelow) {
        // scroll up by half of 40% (.4 / 2 = 0.2) (approx height of mobile modal that appears when choosing mloc)
        map.panBy(0, window.innerHeight * 0.2);
      } else {
        // scroll by half of 60vw (.6 / 2 = 0.3) to accomodate provider drawer
        map.panBy(-1 * window.innerWidth * 0.3, 0);
      }
    }
    log(CLICK_MAP_LOCATION_PIN, {
      mlocName: mloc.name,
      mlocId: mloc.id
    });
  };

  const mapLocations = ((locations || {})._locations || []).map(
    formatLocationForGoogleMap
  );

  const isMachineInLoadingState =
    state.matches(INITIALIZING) ||
    state.matches(FETCHING_PROVIDERS_AND_LOCATIONS) ||
    state.matches(
      `${SORT_AND_FILTER_DRAWER_OPEN}.${FETCHING_PROVIDERS_AND_LOCATIONS}`
    ) ||
    state.matches(FETCH_PROVIDERS) ||
    state.matches(`${SORT_AND_FILTER_DRAWER_OPEN}.${FETCH_PROVIDERS}`);

  // the loading overlay for small screens needs to cover the whole map when fetching
  // providers. when loading more on small screens though, the overlay should be in
  // the bottom sheet, so that is done in SmallMediumView.jsx
  const isSmallViewInLoadingState =
    isScreenMediumOrBelow &&
    state.matches(`${PROVIDER_DRAWER_OPEN}.${FETCHING_PROVIDERS}`);

  const backToSearchUrl = getBackToListView(
    currentQuery,
    OMIT_QUERY_PARAMS_BACK_TO_SEARCH
  );

  let modal;
  if (
    (state.matches(NO_RESULTS) ||
      state.matches(`${SORT_AND_FILTER_DRAWER_OPEN}.${NO_RESULTS}`)) &&
    !shouldShowNoResultsBanner
  ) {
    modal = (
      <MapErrorModal
        errorType="NO_RESULTS"
        backToSearchUrl={backToSearchUrl}
        showBackToSearchResultLink={true}
        messages={messages}
        log={log}
      />
    );
  }
  if (state.matches(`${SORT_AND_FILTER_DRAWER_OPEN}.${NO_MLOC_DATA}`)) {
    modal = (
      <MapErrorModal
        errorType="NO_RESULTS"
        backToSearchUrl={backToSearchUrl}
        showBackToSearchResultLink={true}
        hasNoMlocData={true}
        messages={messages}
        log={log}
      />
    );
  }
  if (state.matches(ERROR)) {
    modal = <MapErrorModal messages={messages} log={log} />;
  }

  const mapContent =
    showMapInProviderList && isScreenMediumOrBelow ? (
      <SmallMediumProvidersMapView
        isScreenMediumOrBelow={isScreenMediumOrBelow}
        selectedLocation={selectedLocation}
        send={send}
        streetViewMapEvents={streetViewMapEvents}
        config={config}
        appSettings={appSettings}
        state={state}
        history={history}
        log={log}
        location={location}
        match={match}
        getUpdatedSearch={getUpdatedLocations}
        isStreetViewOpen={isStreetViewOpen}
      />
    ) : (
      <>
        {
          // hide the "<- Back to List View" and "Sort and Filter" controls
          // when Street View is open
          !isStreetViewOpen && (
            <MapControls
              backToSearchUrl={backToSearchUrl}
              state={state}
              send={send}
              log={log}
              onFiltersOpened={() => {
                if (gmapRef.current && selectedLocation) {
                  // recenter map
                  gmapRef.current.panTo(gMapLatLngForMloc(selectedLocation));
                  // pan by half the width of the filter drawer (20vw / 2 = 0.1)
                  gmapRef.current.panBy(window.innerWidth * 0.1, 0);
                }
              }}
              filtersEnabled={
                facetConfigure.current.getConfiguredFacets(
                  providers?.facets,
                  currentSearchQuery
                ).length > 0
              }
            />
          )
        }

        <DynamicGmap
          // re-render map when viewport has changed to re-bind event handlers
          key={isScreenMediumOrBelow ? 'medOrBelow' : 'large'}
          locations={mapLocations}
          enabledLocationIds={new Set(mapLocations.map((loc) => loc.id))}
          selectedLocation={selectedLocation}
          mapEventsToRegister={{
            dragend: (mapRef) => {
              if (mapRef.current?.getCenter()) {
                const lat = mapRef.current.getCenter().lat();
                const lng = mapRef.current.getCenter().lng();
                const zoom = mapRef.current.zoom;
                /* eslint-disable-next-line no-console */
                console.log('map.dragend ', { lat, lng, zoom });
              }
            },
            bounds_changed: (mapRef) => {
              // set current map zoom value
              getMapZoomRefOnLoad.current = mapRef.current.zoom;
            },
            zoom_changed: (mapRef) => {
              if (mapRef.current?.getCenter()) {
                const lat = mapRef.current.getCenter().lat();
                const lng = mapRef.current.getCenter().lng();
                const zoom = mapRef.current.zoom;
                /* eslint-disable-next-line no-console */
                console.log('map.zoom_changed ', { lat, lng, zoom });
              }
            },
            ...streetViewMapEvents
            // disabled for V1 of map search https://kyruus.jira.com/browse/KENG-29840
            // bounds_changed: onBoundsChanged
          }}
          shouldLabelMarkers={false}
          onMarkerClick={onPinClick}
          mapOptions={{
            maxZoom: 16,
            fullscreenControl: false,
            mapTypeControl: false,
            styles: [
              {
                featureType: 'poi',
                stylers: [{ visibility: 'off' }]
              }
            ]
          }}
          theme={config.theme}
        />

        {isScreenMediumOrBelow ? (
          <SmallMediumView
            intl={intl}
            state={state}
            send={send}
            onLocationLinkClick={onLocationLinkClick}
            currentQuery={currentQuery}
            config={config}
            searchSummary={searchParams}
            sortOptions={sortOptions}
            getUpdatedLocations={getUpdatedLocations}
            log={log}
            location={location}
            customerCode={customerCode}
            productName={productName}
            gmapRef={gmapRef}
            slotsByProviderId={slotsByProviderId}
            slotsStatusByProviderId={slotsStatusByProviderId}
            fetchPurposesByProviderId={fetchPurposesByProviderId}
            fetchProviderAvailability={fetchProviderAvailability}
            purposeOptionsByProviderId={purposeOptionsByProviderId}
            availabilityError={availabilityError}
            purposesLoading={purposesLoading}
          />
        ) : (
          <LargeView
            intl={intl}
            state={state}
            send={send}
            onLocationLinkClick={onLocationLinkClick}
            currentQuery={currentQuery}
            config={config}
            searchSummary={searchSummary}
            sortOptions={sortOptions}
            getUpdatedLocations={getUpdatedLocations}
            searchParams={searchParams}
            log={log}
            location={location}
            customerCode={customerCode}
            productName={productName}
            gmapRef={gmapRef}
            slotsByProviderId={slotsByProviderId}
            slotsStatusByProviderId={slotsStatusByProviderId}
            fetchPurposesByProviderId={fetchPurposesByProviderId}
            fetchProviderAvailability={fetchProviderAvailability}
            purposeOptionsByProviderId={purposeOptionsByProviderId}
            availabilityError={availabilityError}
            purposesLoading={purposesLoading}
          />
        )}
      </>
    );

  return (
    <MapContainer
      isCCF={isCCF()}
      data-testid="MapContainer"
      ref={mapContainerRef}
    >
      <h1 className="sr-only">
        {intl.formatMessage(searchV9Messages.mapSearchLinkAriaLabel)}
      </h1>
      <LoadingOverlay
        loading={isMachineInLoadingState || isSmallViewInLoadingState}
      />

      {/* this is `null` when Street View is closed */ streetViewCloseButton}

      {/* disabled for V1 of map search https://kyruus.jira.com/browse/KENG-29840 */}
      {/* {showSearchThisAreaCTA && (
        <SearchThisArea onClick={onSearchThisAreaClick} />
      )} */}

      {mapContent}

      {providers?.facets?.length &&
        // render only when click on sort & filter button
        state.matches(`${SORT_AND_FILTER_DRAWER_OPEN}`) && (
          <SideDrawerFilterPanel
            config={config}
            facets={facetConfigure.current.getConfiguredFacets(
              providers.facets,
              currentSearchQuery
            )}
            getUpdatedSearch={getUpdatedLocations}
            loading={isMachineInLoadingState || isSmallViewInLoadingState}
            log={log}
            onClose={() => {
              send(CLOSE_SORT_AND_FILTER);
            }}
            searchSummary={searchParams}
            showLocationFacet={false}
            showMobileFacets={true}
          />
        )}
      {modal}
      <MapSearchMetas search={searchParams} />
    </MapContainer>
  );
}

export default injectIntl(MapSearch);
